import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getExpectedUserType } from "../utils/authScope";
import { UserType } from "../api/authService";
import { collectVisitorInfo } from "../utils/visitorInfo";
import { recordVisitorSession, recordPageView } from "../api/visitorService";

const SESSION_FLAG = "bigmarket_visit_recorded";

function alreadyRecordedThisSession() {
  try {
    return sessionStorage.getItem(SESSION_FLAG) === "1";
  } catch {
    return false;
  }
}

function markRecorded() {
  try {
    sessionStorage.setItem(SESSION_FLAG, "1");
  } catch {
    // ignore
  }
}

// Renders nothing. On the client-facing site it (1) registers the visitor as a guest automatically,
// like the mobile app does on launch, (2) records device/browser/referrer info once per browser
// session, and (3) logs every page view. Staff areas (admin, business owner, delivery) are skipped.
export default function VisitorTracker() {
  const { pathname } = useLocation();
  const { registerGuestIfNone } = useAuth();
  const sessionStarted = useRef(false);
  const previousPath = useRef(null);

  useEffect(() => {
    if (getExpectedUserType(pathname) !== UserType.User) return;

    (async () => {
      // The session must go first so the guest's token is attached to the calls below.
      if (!sessionStarted.current) {
        sessionStarted.current = true;
        try {
          await registerGuestIfNone();
        } catch {
          // Guest creation is best-effort here; actions like add-to-cart retry it via ensureSession.
        }
        if (!alreadyRecordedThisSession()) {
          markRecorded();
          await recordVisitorSession(collectVisitorInfo());
        }
      }

      // In-app navigation's "referrer" is the previous page; the first view uses the external one.
      const referrer = previousPath.current ?? collectVisitorInfo().referrer;
      previousPath.current = pathname;
      recordPageView(pathname, referrer);
    })();
    // registerGuestIfNone is stable per role; re-running only on navigation is intended.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return null;
}
